import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  SalesActivity, ActivitySummary, CreateActivityInput, ListActivitiesFilters,
  listActivities, createActivity, updateActivity, deleteActivity, getActivitySummary,
} from '../services/salesActivitiesApi';

interface State {
  items: SalesActivity[];
  summary: ActivitySummary | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchList: (filters?: ListActivitiesFilters) => Promise<void>;
  fetchSummary: (days?: number) => Promise<void>;
  create: (input: CreateActivityInput) => Promise<SalesActivity>;
  update: (id: number, input: Partial<CreateActivityInput>) => Promise<SalesActivity>;
  remove: (id: number) => Promise<void>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useSalesActivitiesStore = create<State>()(
  devtools(
    (set) => ({
      items: [],
      summary: null,
      loading: false,
      saving: false,
      error: null,
      fetchList: async (filters) => {
        set({ loading: true, error: null });
        try {
          const result = await listActivities(filters);
          set({ items: result.data, loading: false });
        } catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchSummary: async (days) => {
        try { set({ summary: await getActivitySummary(days) }); }
        catch (err) { set({ error: toErr(err) }); }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createActivity(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) { set({ saving: false, error: toErr(err) }); throw err; }
      },
      update: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateActivity(id, input);
          set((s) => ({
            items: s.items.map((a) => (a.id === id ? updated : a)),
            saving: false,
          }));
          return updated;
        } catch (err) { set({ saving: false, error: toErr(err) }); throw err; }
      },
      remove: async (id) => {
        set({ saving: true, error: null });
        try {
          await deleteActivity(id);
          set((s) => ({ items: s.items.filter((a) => a.id !== id), saving: false }));
        } catch (err) { set({ saving: false, error: toErr(err) }); throw err; }
      },
    }),
    { name: 'sales-activities-store' }
  )
);
